/**
 * React hooks for Symptoms API
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { symptomsApi } from '../api/symptoms';
import { toast } from 'sonner';

/**
 * Get symptom history for current patient user (no patient_id needed)
 */
export function useMySymptomHistory(days: number = 30) {
  return useQuery({
    queryKey: ['my-symptoms', days],
    queryFn: () => symptomsApi.getMySymptomHistory(days),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * Get common triggers for current patient user
 */
export function useMyCommonTriggers() {
  return useQuery({
    queryKey: ['my-symptom-triggers'],
    queryFn: () => symptomsApi.getMyCommonTriggers(),
    staleTime: 1000 * 60 * 60, // 1 hour (triggers don't change often)
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * Log symptoms for current patient user
 */
export function useLogMySymptoms() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      patient_id: number;
      date: string;
      symptoms: {
        breathlessness: number;
        energy_level: number;
        cough: number;
        sleep_quality: number;
        chest_pain: number;
        fatigue: number;
      };
      triggers?: string[];
      additional_notes?: string;
    }) => symptomsApi.logMySymptoms(data),
    
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-symptoms'] });
      toast.success('Symptoms logged successfully');
    },
    
    onError: (error: any) => {
      toast.error(error.message || 'Failed to log symptoms');
    },
  });
}

export function useSymptomHistory(patientId: number, days: number = 30) {
  return useQuery({
    queryKey: ['symptoms', patientId, days],
    queryFn: () => symptomsApi.getSymptomHistory(patientId, days),
    enabled: !!patientId && patientId > 0, // ✅ FIX: Only fetch when we have a valid patient ID
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

export function useLogSymptoms(patientId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      patient_id: number;
      date: string;
      symptoms: {
        breathlessness: number;
        energy_level: number;
        cough: number;
        sleep_quality: number;
        chest_pain: number;
        fatigue: number;
      };
      triggers?: string[];
      additional_notes?: string;
    }) => symptomsApi.logMySymptoms(data),  // Use logMySymptoms instead
    
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['symptoms', patientId] });
      toast.success('Symptoms logged successfully');
    },
    
    onError: (error: any) => {
      toast.error(error.message || 'Failed to log symptoms');
    },
  });
}

export function useCommonTriggers(patientId: number) {
  return useQuery({
    queryKey: ['symptom-triggers', patientId],
    queryFn: () => symptomsApi.getCommonTriggers(patientId),
    enabled: !!patientId && patientId > 0, // ✅ FIX: Only fetch when we have a valid patient ID
    staleTime: 1000 * 60 * 60, // 1 hour (triggers don't change often)
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * ✅ NEW: Update symptom log for current patient user
 */
export function useUpdateMySymptom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ symptomId, data }: {
      symptomId: number;
      data: {
        symptoms?: {
          breathlessness: number;
          energy_level: number;
          cough: number;
          sleep_quality: number;
          chest_pain: number;
          fatigue: number;
        };
        triggers?: string[];
        additional_notes?: string;
      };
    }) => symptomsApi.updateMySymptom(symptomId, data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-symptoms'] });
      toast.success('Symptom log updated successfully');
    },

    onError: (error: any) => {
      toast.error(error.message || 'Failed to update symptom log');
    },
  });
}

/**
 * ✅ NEW: Delete symptom log for current patient user
 */
export function useDeleteMySymptom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (symptomId: number) => symptomsApi.deleteMySymptom(symptomId),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-symptoms'] });
      toast.success('Symptom log deleted successfully');
    },

    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete symptom log');
    },
  });
}

/**
 * ✅ ALL-IN-ONE: Hook for symptoms management with CRUD operations
 */
export function useSymptoms(patientId: number) {
  const queryClient = useQueryClient();

  // Get symptoms data (last 7 days)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['my-symptoms', 7],
    queryFn: () => symptomsApi.getMySymptomHistory(7),
    enabled: !!patientId && patientId > 0, // ✅ FIX: Only fetch when we have a valid patient ID
    staleTime: 1000 * 60 * 5,
    retry: false, // ✅ FIX: Don't retry on auth errors
  });

  // Log symptom mutation
  const logSymptom = useMutation({
    mutationFn: (symptomData: any) => symptomsApi.recordMySymptom(symptomData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-symptoms'] });
    },
  });

  // Update symptom mutation
  const updateSymptom = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      symptomsApi.updateMySymptom(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-symptoms'] });
    },
  });

  // Delete symptom mutation
  const deleteSymptom = useMutation({
    mutationFn: (symptomId: number) => symptomsApi.deleteMySymptom(symptomId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-symptoms'] });
    },
  });

  return {
    data,
    isLoading,
    error,
    refetch,
    logSymptom,
    updateSymptom,
    deleteSymptom,
  };
}